import React, { useRef, useEffect } from 'react';
import { initWebGLFx, applyFxToCanvas, disposeFxCache } from './webglFx';
import type { FxUniforms } from './webglFx';

export interface WebGLFxLayerProps {
  width: number;
  height: number;
  uniforms: FxUniforms;
  enabled: boolean;
  sourceCanvas?: HTMLCanvasElement | null;
}

export function WebGLFxLayer({ width, height, uniforms, enabled, sourceCanvas }: WebGLFxLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGL2RenderingContext | null>(null);

  // Initialize WebGL2 context once on mount
  useEffect(() => {
    if (!canvasRef.current) return;
    glRef.current = initWebGLFx(canvasRef.current);
    return () => {
      // Dispose cached shader program + GPU resources before losing context
      disposeFxCache();
      // Explicitly release the WebGL context to free GPU memory on unmount
      glRef.current?.getExtension('WEBGL_lose_context')?.loseContext();
      glRef.current = null;
    };
  }, []);

  // Re-render when any relevant prop changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!enabled || !sourceCanvas) return;

    const gl = glRef.current;
    if (!gl) {
      canvas.style.display = 'none';
      return;
    }

    createImageBitmap(sourceCanvas)
      .then((bitmap) => {
        const success = applyFxToCanvas(gl, bitmap, uniforms);
        bitmap.close();
        if (!success) {
          canvas.style.display = 'none';
        } else {
          canvas.style.display = '';
        }
      })
      .catch(() => {
        canvas.style.display = 'none';
      });
  }, [enabled, sourceCanvas, uniforms, width, height]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
